import { useLazyQuery, useMutation } from '@apollo/client';
import {
  ContextualMenu,
  IDragOptions,
  makeStyles,
  Modal,
  Stack,
} from '@fluentui/react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { NotificationCycleRowItem } from '../../list';
import { BasicForm } from './BasicForm';
import { Footer } from './Footer';
import GET_DEFAULT_TIMEZONE from './GetDefaultTimezone.graphql';
import { Header } from './Header';
import NOTIFY_TIME_CREATE from './NotifyTimeCreate.graphql';
import NOTIFY_TIME_UPDATE from './NotifyTimeUpdate.graphql';
import {
  NotifyTimeCreate,
  NotifyTimeCreateVariables,
} from './__generated__/NotifyTimeCreate';
import { NotificationCycleValues } from './types';

import { yupResolver } from '@hookform/resolvers/yup';
import { showCustomToast } from 'common/components/CustomToast/utils';
import {
  NotifyTimeCreateInput,
  NotifyTimeUpdateInput,
} from 'common/types/globalTypes';
import NOTIFY_TIMES_FIELDS from '../../NotifyTimeFields.graphql';
import { NOTIFY_TIME_VALUES } from '../constants';
import { GetDefaultTimezone } from './__generated__/GetDefaultTimezone';
import {
  NotifyTimeUpdate,
  NotifyTimeUpdateVariables,
} from './__generated__/NotifyTimeUpdate';
import { getDefaultValues } from './utils';
import { validationSchema } from './validation';

const NOTIFY_TIME_FRAGMENT_NAME = 'NotifyTimeFields';

const DragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
  dragHandleSelector: '.ms-Modal-scrollableContent > div > div:first-child ',
};

const useStyles = makeStyles(() => ({
  container: {
    width: 800,
    maxHeight: 700,
  },
}));

type NotificationCycleModalProps = {
  notificationCycle?: NotificationCycleRowItem;
  onClose: () => void;
};

export const NotificationCycleModal: React.FC<NotificationCycleModalProps> = ({
  notificationCycle,
  onClose,
}) => {
  const styles = useStyles();
  const formMethods = useForm<NotificationCycleValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });
  const { handleSubmit, reset } = { ...formMethods };
  const { id, _rowTimestamp } = { ...notificationCycle };
  const isEdit = !!id;

  const [createNotifyTime, { loading: createNotifyTimeLoading }] = useMutation<
    NotifyTimeCreate,
    NotifyTimeCreateVariables
  >(NOTIFY_TIME_CREATE, { errorPolicy: 'all' });

  const [updateNotifyTime, { loading: updateNotifyTimeLoading }] = useMutation<
    NotifyTimeUpdate,
    NotifyTimeUpdateVariables
  >(NOTIFY_TIME_UPDATE, { errorPolicy: 'all' });

  const [getDefaultTimeZone, { data: defaultTimeZoneData }] =
    useLazyQuery<GetDefaultTimezone>(GET_DEFAULT_TIMEZONE, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    });

  const onHandleSubmit = async (values: NotificationCycleValues) => {
    if (!!id && !!_rowTimestamp) {
      const { lastRun, _rowTimestamp, ...notifyTimePatch } = values;
      const { errors } = await updateNotifyTime({
        variables: {
          input: {
            id,
            rowTimestamp: _rowTimestamp,
            notifyTimePatch: {
              ...notifyTimePatch,
              notifyTypeId: Number(notifyTimePatch.notifyTypeId),
              timeZoneId: Number(notifyTimePatch.timeZoneId),
            },
          } as NotifyTimeUpdateInput,
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'Notify cycle updated successfully.',
          appearance: 'success',
        });
      }
    } else {
      const { lastRun, _rowTimestamp, ...notifyTime } = values;

      const { errors } = await createNotifyTime({
        variables: {
          input: {
            notifyTime: {
              ...notifyTime,
              notifyTypeId: Number(notifyTime.notifyTypeId),
              timeZoneId: Number(notifyTime.timeZoneId),
            },
          } as NotifyTimeCreateInput,
        },
        update: (cache, { data }) => {
          if (!!data?.notifyTimeCreate?.notifyTime?.id) {
            cache.modify({
              fields: {
                notifyTimes(existing) {
                  if (data?.notifyTimeCreate?.notifyTime) {
                    const newNotifyTimeRef = cache.writeFragment({
                      data: data.notifyTimeCreate.notifyTime,
                      fragment: NOTIFY_TIMES_FIELDS,
                      fragmentName: NOTIFY_TIME_FRAGMENT_NAME,
                    });
                    return {
                      ...existing,
                      nodes: [newNotifyTimeRef, ...existing.nodes],
                    };
                  }
                },
              },
            });
          }
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'Notify cycle created successfully.',
          appearance: 'success',
        });
        onClose();
      }
    }
  };
  useEffect(() => {
    if (notificationCycle) {
      const defaultValues = getDefaultValues({
        notificationCycle,
      });
      reset(defaultValues);
    }
  }, [notificationCycle, reset]);

  useEffect(() => {
    if (!notificationCycle) getDefaultTimeZone();
  }, []);

  useEffect(() => {
    if (defaultTimeZoneData) {
      const defaultValues: NotificationCycleValues = {
        ...NOTIFY_TIME_VALUES,
        timeZoneId:
          defaultTimeZoneData?.notifyTimes?.nodes[0]?.timeZoneId ?? null,
      };
      reset(defaultValues);
    }
  }, [defaultTimeZoneData]);

  return (
    <Modal isOpen isBlocking dragOptions={DragOptions}>
      <Stack className={styles.container}>
        <FormProvider {...formMethods}>
          <Header
            onClose={onClose}
            title={
              notificationCycle
                ? 'Update Notification Cycle'
                : 'Create Notification Cycle'
            }
            isEdit={isEdit}
          />
          <BasicForm
            isUpdatable={
              !notificationCycle || !!notificationCycle?._isUpdatable
            }
          />
          <Footer
            loading={createNotifyTimeLoading || updateNotifyTimeLoading}
            onSubmit={handleSubmit(onHandleSubmit)}
            onCancel={onClose}
          />
        </FormProvider>
      </Stack>
    </Modal>
  );
};
